// src/features/error-definitions/components/ErrorDefinitionActions.tsx

import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
} from '@mui/icons-material';
import { useTranslate, useDataProvider, useNotify } from 'react-admin';
import type { ErrorDefinition } from '@/types/errorDefinitions';

interface ErrorDefinitionActionsProps {
  record: ErrorDefinition;
  onEdit: (record: ErrorDefinition) => void;
  onDeleted: () => void;
}

/**
 * Row actions for Error Definitions list
 * Shows menu with Edit and Delete options
 */
const ErrorDefinitionActions: React.FC<ErrorDefinitionActionsProps> = ({
  record,
  onEdit,
  onDeleted,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const open = Boolean(anchorEl);

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  // Action handlers
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    onEdit(record);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      // DELETE expects GUID id, not errorCode
      if (!record.id) {
        throw new Error('Cannot delete: missing definition ID');
      }
      await dataProvider.deleteErrorDefinition(record.id);
      notify('resources.errorDefinitions.notifications.deleted', { type: 'success' });
      handleDeleteDialogClose();
      onDeleted();
    } catch (error: any) {
      const errorMessage = error?.message || translate('resources.errorDefinitions.errors.deleteFailed');
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Tooltip title={translate('ra.action.menu')}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ color: 'action.active' }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        id="error-definition-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={(e: any) => handleClose(e)}
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{
          'aria-labelledby': 'error-definition-actions-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Edit */}
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('ra.action.edit')}</ListItemText>
        </MenuItem>

        {/* Delete */}
        <MenuItem
          onClick={handleDeleteClick}
          sx={{
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'error.light',
            },
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{translate('ra.action.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          {translate('ra.message.delete_title', { name: record.errorCode })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate('ra.message.delete_content', {
              name: record.errorCode,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={isDeleting}>
            {translate('ra.action.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            autoFocus
          >
            {isDeleting
              ? translate('ra.action.loading')
              : translate('ra.action.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ErrorDefinitionActions;
